import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
});

// ---------------------------------------------------------------------------
// Bearer-token fallback
// ---------------------------------------------------------------------------
// httpOnly cookies are our primary auth mechanism, but some browsers (Safari
// ITP, Brave strict, Chrome incognito) block cross-site/third-party cookies.
// In those cases we fall back to an Authorization: Bearer header sourced from
// the JWT that the backend returns in the login / change-password / refresh
// response body. The cookie remains the source of truth when allowed.
// ---------------------------------------------------------------------------
const TOKEN_KEY = 'raum_access_token';

export const setAuthToken = (token) => {
  if (token) {
    try { localStorage.setItem(TOKEN_KEY, token); } catch { /* storage disabled */ }
  } else {
    try { localStorage.removeItem(TOKEN_KEY); } catch { /* storage disabled */ }
  }
};

export const getAuthToken = () => {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
};

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => {
    // Capture any fresh access_token returned by the backend so the Bearer
    // fallback stays in sync after login / password change / refresh.
    const token = response?.data?.access_token;
    if (token && typeof token === 'string') setAuthToken(token);
    return response;
  },
  (error) => Promise.reject(error)
);

export const formatApiError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export const formatZAR = (amount) => {
  const num = Number(amount);
  if (num >= 1000000) return `R${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `R${(num / 1000).toFixed(0)}K`;
  return `R${num.toLocaleString('en-ZA')}`;
};

export const formatZARFull = (amount) => `R ${Number(amount).toLocaleString('en-ZA', { maximumFractionDigits: 0 })}`;

export const trackEvent = async (eventType, data = {}) => {
  try {
    await api.post('/analytics/event', {
      event_type: eventType,
      page: window.location.pathname,
      data,
      session_id: sessionStorage.getItem('ruam_session') || (() => {
        const id = crypto.randomUUID();
        sessionStorage.setItem('ruam_session', id);
        return id;
      })()
    });
  } catch {
    // Silent fail for analytics
  }
};

export const LOCATIONS = [
  { value: 'cape_town', label: 'Cape Town' },
  { value: 'johannesburg', label: 'Gauteng' },
  { value: 'pretoria', label: 'Pretoria' },
  { value: 'durban', label: 'Durban' },
  { value: 'garden_route', label: 'Garden Route' },
  { value: 'stellenbosch', label: 'Stellenbosch' },
  { value: 'bloemfontein', label: 'Bloemfontein' },
  { value: 'port_elizabeth', label: 'Port Elizabeth / Gqeberha' },
  { value: 'other', label: 'Other' },
];

export const FINISH_LEVELS = [
  { value: 'economy', label: 'Economy', desc: 'R12K-R16K/m²' },
  { value: 'standard', label: 'Standard', desc: 'R16K-R22K/m²' },
  { value: 'premium', label: 'Premium', desc: 'R22K-R30K/m²' },
  { value: 'luxury', label: 'Luxury', desc: 'R30K-R50K/m²' },
];

export const PROJECT_TYPES = [
  { value: 'new_build', label: 'New Build' },
  { value: 'renovation', label: 'Renovation' },
  { value: 'addition', label: 'Addition' },
  { value: 'alteration', label: 'Alteration / Upgrade' },
];

export const BUILDING_TYPES = [
  { group: 'Residential', options: [
    { value: 'house', label: 'House (single dwelling)' },
    { value: 'apartment', label: 'Apartment / Multi-unit' },
    { value: 'outbuilding', label: 'Outbuilding (garage, carport, patio)' },
  ]},
  { group: 'Commercial', options: [
    { value: 'office', label: 'Office' },
    { value: 'retail', label: 'Retail' },
    { value: 'warehouse', label: 'Warehouse / Industrial' },
  ]},
  { group: 'Institutional', options: [
    { value: 'school', label: 'School / Campus' },
    { value: 'healthcare', label: 'Healthcare (clinic / hospital)' },
  ]},
  { group: 'Other', options: [
    { value: 'mixed_use', label: 'Mixed-use' },
    { value: 'custom', label: 'Custom / Other' },
  ]},
];

export default api;
